import React, { useEffect, useState } from "react";
import * as SliderPrimitive from "@radix-ui/react-slider";
import { cn } from "@/lib/utils"; // Assuming you have a utility function for classnames

type SliderProps = {
  min: number;
  max: number;
  step: number;
  showNewPoints?: boolean;
  value?: number[] | readonly number[];
  onValueChange?: (values: number[]) => void;
  className?: string;
};

const Slider = React.forwardRef<HTMLDivElement, SliderProps>(
  (
    {
      className,
      min,
      max,
      step,
      showNewPoints,
      value,
      onValueChange,
      ...props
    }: SliderProps,
    ref
  ) => {
    const initialValue = Array.isArray(value) ? value : [min, max];
    const [localValues, setLocalValues] = useState(initialValue);

    useEffect(() => {
      // Update localValues when the external value prop changes
      setLocalValues(Array.isArray(value) ? value : [min, max]);
    }, [min, max, value]);

    const handleValueChange = (newValues: number[]) => {
      setLocalValues(newValues);
      if (onValueChange) {
        onValueChange(newValues);
      }
    };

    const formatValue = ({
      value,
      showPoints,
    }: {
      value: number;
      showPoints?: boolean;
    }) => {
      if (showPoints) {
        // const isFloat = (value).toString().includes(".");
        return <span className="whitespace-nowrap">{`${value} лв`}</span>;
        // return isFloat || true
        //   ? `$${(value).toFixed(3)}`
        //   : `$${value}`;
      }
      return <span className="text-gray text-xs">{Math.round(value)} лв</span>;
    };

    return (
      <div className="relative w-full">
        <SliderPrimitive.Root
          ref={ref}
          min={min}
          max={max}
          step={step}
          value={localValues}
          onValueChange={handleValueChange}
          className={cn(
            "relative flex w-full touch-none select-none items-center",
            className
          )}
          {...props}
        >
          <SliderPrimitive.Track className="relative h-1.5 w-full grow overflow-hidden rounded-full bg-primary/20">
            <SliderPrimitive.Range className="absolute h-full bg-primary" />
          </SliderPrimitive.Track>
          {localValues.map((value, index) => {
            const valueInPercent = ((value - min) / (max - min)) * 100;

            return (
              <React.Fragment key={index}>
                <SliderPrimitive.Thumb className="block h-4 w-4 rounded-full border border-primary/50 bg-background shadow transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50" />
                <div
                  className="absolute text-center"
                  style={{
                    left: `calc(${valueInPercent}% + ${
                      -10 + (valueInPercent / 100) * (-30 - -10)
                    }px)`,
                    top: 12,
                  }}
                >
                  {showNewPoints ? (
                    <span className="text-sm text-black">
                      {formatValue({ value, showPoints: true })}
                    </span>
                  ) : (
                    <span className="text-sm ml-2 text-text-black">
                      {value}
                    </span>
                  )}
                </div>
              </React.Fragment>
            );
          })}
        </SliderPrimitive.Root>
        <div className="flex justify-between mt-[-35px]">
          <span className="text-sm text-primary">
            {showNewPoints ? formatValue({ value: min }) : min}
          </span>
          <span className="text-sm text-primary">
            {showNewPoints ? formatValue({ value: max }) : max}
          </span>
        </div>
      </div>
    );
  }
);

Slider.displayName = "Slider"; // Set a display name for easier debugging

export { Slider };
